import * as React from 'react';
import { z } from 'zod/v4';
import {
  AddressAutocompleteInput,
  type AddressInputLabels,
  type AddressInputPlaceholders,
  type AddressAutocompleteInputProps,
  type AddressSuggestion,
  type AddressSuggestionsProvider,
  type AddressDetailsResolver,
  type AddressValue,
} from './address-autocomplete-input';
import {
  FRENCH_DEPARTMENT_OPTIONS,
  type FrenchDepartmentCode,
} from './french-departments';
import { Label } from './label';
import { Select } from './select';

const zFrenchAddressResponse = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(
    z.object({
      type: z.literal('Feature'),
      geometry: z.object({
        type: z.literal('Point'),
        coordinates: z.tuple([z.number(), z.number()]),
      }),
      properties: z.object({
        id: z.string(),
        label: z.string(),
        name: z.string(),
        postcode: z.string(),
        city: z.string(),
        housenumber: z.string().optional(),
        street: z.string().optional(),
        context: z.string().optional(),
        depcode: z.string(),
      }),
    }),
  ),
});

type FrenchAddressSuggestion = AddressSuggestion<AddressValue>;

const frenchAddressCache = new Map<string, AddressValue>();
const frenchDepartmentCodes = new Set<string>(
  FRENCH_DEPARTMENT_OPTIONS.map((option) => option.value),
);

function formatFrenchAddressQuery(query: string): string {
  const match = query.match(
    /^(.+?)\s+(\d{1,4}(?:[-/]\d{1,4})?(?:\s*(?:bis|ter|quater|quinquies|[a-z]))?)(\s*,.*)?$/i,
  );
  if (!match) return query;

  const [, street, streetNumber, locality = ''] = match;
  return `${streetNumber} ${street}${locality}`;
}

async function fetchFrenchAddressFeatures(query: string, signal: AbortSignal) {
  const parameters = new URLSearchParams({
    q: query,
    autocomplete: '1',
    index: 'address',
    limit: '5',
  });
  const response = await fetch(
    `https://data.geopf.fr/geocodage/search?${parameters.toString()}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      `French address search failed with HTTP ${response.status}.`,
    );
  }

  return zFrenchAddressResponse.parse(await response.json()).features;
}

const frenchAddressSuggestionsProvider: AddressSuggestionsProvider<
  FrenchAddressSuggestion
> = async (query, { signal }) => {
  const formattedQuery = formatFrenchAddressQuery(query);
  let features = await fetchFrenchAddressFeatures(formattedQuery, signal);
  if (
    formattedQuery !== query &&
    !features.some((feature) => feature.properties.housenumber)
  ) {
    features = await fetchFrenchAddressFeatures(query, signal);
  }

  return features.map((feature) => {
    const { properties, geometry } = feature;
    if (!frenchDepartmentCodes.has(properties.depcode)) {
      throw new Error(
        `French address returned unsupported département ${properties.depcode}.`,
      );
    }
    const address: AddressValue = {
      street: properties.street ?? properties.name,
      streetNumber: properties.housenumber ?? '',
      postalCode: properties.postcode,
      city: properties.city,
      country: 'FR',
      department: properties.depcode as FrenchDepartmentCode,
      placeId: properties.id,
      coordinates: {
        lat: geometry.coordinates[1],
        lng: geometry.coordinates[0],
      },
    };
    frenchAddressCache.set(properties.id, address);
    return {
      id: properties.id,
      label: properties.label,
      description: properties.context,
    };
  });
};

const frenchAddressDetailsResolver: AddressDetailsResolver<
  FrenchAddressSuggestion,
  AddressValue
> = async (suggestion) => {
  const address = frenchAddressCache.get(suggestion.id);
  if (!address) {
    throw new Error(`French address details are missing for ${suggestion.id}.`);
  }
  return address;
};

const FRENCH_LABELS: AddressInputLabels = {
  search: 'Adresse',
  manualEntry: "Saisir l'adresse manuellement",
  street: 'Rue',
  streetNumber: 'Numéro',
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  canton: 'Canton',
  department: 'Département',
  searching: 'Recherche...',
  noOptions: 'Aucune adresse trouvée',
  results: '{count} résultats',
  countrySearch: 'Rechercher...',
  noCountries: 'Aucun pays trouvé.',
};

const FRENCH_PLACEHOLDERS: AddressInputPlaceholders = {
  search: 'Rechercher une adresse',
  street: 'Rue',
  streetNumber: 'Numéro',
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'France',
  canton: 'Sélectionner un canton',
  department: 'Sélectionner un département',
};

export type FrenchAddressInputProps = Omit<
  AddressAutocompleteInputProps<AddressValue, FrenchAddressSuggestion>,
  | 'suggestionsProvider'
  | 'detailsResolver'
  | 'allowedCountries'
  | 'fixedCountry'
  | 'defaultCountry'
  | 'autocompleteCountries'
  | 'debounceMs'
  | 'minimumQueryLength'
  | 'renderRegionField'
  | 'onEdit'
  | 'resolving'
>;

/** Controlled French address autocomplete with manual entry as a fallback. */
const FrenchAddressInput = React.forwardRef<
  HTMLInputElement,
  FrenchAddressInputProps
>(({ labels, placeholders, ...props }, ref) => (
  <AddressAutocompleteInput
    ref={ref}
    {...props}
    suggestionsProvider={frenchAddressSuggestionsProvider}
    detailsResolver={frenchAddressDetailsResolver}
    fixedCountry="FR"
    defaultCountry="FR"
    autocompleteCountries={['FR']}
    allowedCountries={['FR']}
    debounceMs={250}
    minimumQueryLength={3}
    labels={{ ...FRENCH_LABELS, ...labels }}
    placeholders={{ ...FRENCH_PLACEHOLDERS, ...placeholders }}
    renderRegionField={({
      value,
      updateValue,
      disabled,
      isRequired,
      disablePortal,
      baseId,
      name,
      errors,
      labels: mergedLabels,
      placeholders: mergedPlaceholders,
    }) => (
      <div className="grid gap-1.5">
        <Label
          htmlFor={`${baseId}-department`}
          required={isRequired('department')}
        >
          {mergedLabels.department}
        </Label>
        <Select
          id={`${baseId}-department`}
          options={[...FRENCH_DEPARTMENT_OPTIONS]}
          value={value.department}
          onChange={(department) =>
            updateValue({ department: department as FrenchDepartmentCode })
          }
          placeholder={mergedPlaceholders.department}
          disabled={disabled}
          error={Boolean(errors.department)}
          disablePortal={disablePortal}
          searchable
        />
        {name && (
          <input
            type="hidden"
            name={`${name}.department`}
            value={value.department ?? ''}
            disabled={disabled}
          />
        )}
        {typeof errors.department === 'string' && (
          <p className="text-sm text-destructive">{errors.department}</p>
        )}
      </div>
    )}
  />
));

FrenchAddressInput.displayName = 'FrenchAddressInput';

export {
  FrenchAddressInput,
  frenchAddressDetailsResolver,
  frenchAddressSuggestionsProvider,
  zFrenchAddressResponse,
};
